import { MediaSnippet, P, Price, ReviewScore, type ReviewScoreProps } from '@pcln/horizon'

import type { HotelDealMatch } from '@/components/TripActivity/types'

type SimilarHotelMediaSnippetProps = {
  hotel: HotelDealMatch
  onClick?: () => void
}

export function SimilarHotelMediaSnippet({ hotel, onClick }: Readonly<SimilarHotelMediaSnippetProps>) {
  const { linkUrl, reference } = hotel
  const { name, thumbnailUrl, location, overallGuestRating, totalReviewCount, ratesSummary, starRating } =
    reference

  const savingsAmount = ratesSummary?.grandTotalSavingsAmountExcludingTax
  const discountText = savingsAmount
    ? `${ratesSummary?.minCurrencyCodeSymbol}${Math.round(Number(savingsAmount))} Off`
    : undefined

  // ReviewScore has no responsive props of its own — size, reviewCount formatting, and
  // whether reviewLabel shows all need to flip together at `lg`. Resolving that with a
  // media-query hook means rendering the wrong format until client JS hydrates; a plain
  // CSS breakpoint swap (via `lg:hidden` / `hidden lg:block`) is correct on first paint
  // with no JS, so render both variants and let CSS pick the visible one. Mapped over a
  // config array instead of two literal blocks to keep the duplication contained.
  const reviewScoreBreakpoints: Array<
    Pick<ReviewScoreProps, 'size' | 'reviewCount' | 'reviewLabel'> & { wrapperClassName: string }
  > = [
    {
      wrapperClassName: 'lg:hidden',
      size: 'xs',
      reviewCount: totalReviewCount ? `(${totalReviewCount})` : undefined,
    },
    {
      wrapperClassName: 'hidden lg:block',
      size: 'sm',
      reviewCount: totalReviewCount ? String(totalReviewCount) : undefined,
      reviewLabel: totalReviewCount ? 'Reviews' : undefined,
    },
  ]

  return (
    <div className='w-41 lg:w-76'>
      <MediaSnippet
        role='link'
        href={linkUrl}
        onClick={onClick}
        anchorProps={{ target: '_blank', rel: 'noopener noreferrer' }}
        ratio='1/1'
        alt={name}
        imageUrl={thumbnailUrl ?? undefined}
        caption={name}
        slots={{
          captionSlot: 'line-clamp-2 text-heading6 lg:text-heading4',
        }}
        discountText={discountText}
      >
        <div className='flex flex-col gap-1'>
          {location?.neighborhoodName && (
            <P textStyle='body3' className='lg:text-body2'>
              {location.neighborhoodName}
            </P>
          )}
          {overallGuestRating > 0 &&
            reviewScoreBreakpoints.map(({ wrapperClassName, ...reviewScoreProps }) => (
              <div key={wrapperClassName} className={wrapperClassName}>
                <ReviewScore
                  rating={String(overallGuestRating)}
                  data={`${starRating}-Star`}
                  {...reviewScoreProps}
                />
              </div>
            ))}
          {ratesSummary?.grandTotalExcludingTax && (
            <div className='flex items-center gap-1'>
              {ratesSummary.grandTotalStrikeThroughPriceExcludingTax && (
                <Price
                  price={String(Math.round(Number(ratesSummary.grandTotalStrikeThroughPriceExcludingTax)))}
                  currencySymbol={ratesSummary.minCurrencyCodeSymbol}
                  type='urgency'
                  textStyle='body3'
                  isStrike
                />
              )}
              <Price
                price={String(Math.round(Number(ratesSummary.grandTotalExcludingTax)))}
                currencySymbol={ratesSummary.minCurrencyCodeSymbol}
                type='priceSecondary'
                textStyle='heading4'
              />
            </div>
          )}
        </div>
      </MediaSnippet>
    </div>
  )
}
